/**
 * EntityPicker — type-to-search list of existing entities with a
 * "Create new" fallback at the bottom. Used by Capture for:
 *   - Swap-to-another (chip edit sheet → Swap action)
 *   - Inline add when no chip is suggested
 *
 * Loads the tenant's entities lazily on first open + caches per type for
 * the session. Filters as you type (lexical lowercase substring).
 * Hitting Enter on the "Create new" line commits the typed name as a
 * new entity (still routes through preflight A.1 upstream).
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { listEntities, type EntityListItem } from '../api.ts';
import { Avatar } from './primitives';
import { Icons } from './icons';

interface EntityPickerProps {
  type: 'account' | 'contact';
  /** When set, the picker hides this id from the list (the "current"
   * chip's entity for a Swap interaction — you don't swap to yourself). */
  excludeEntityId?: string;
  /** Called with an existing entity when the user picks one. */
  onPickExisting: (entity: EntityListItem) => void;
  /** Called with a typed name when the user wants to create new. */
  onCreateNew: (name: string) => void;
  /** Optional placeholder override. */
  placeholder?: string;
}

let _cache: Partial<Record<'account' | 'contact', EntityListItem[]>> = {};

export function clearEntityPickerCache() {
  _cache = {};
}

export function EntityPicker({
  type,
  excludeEntityId,
  onPickExisting,
  onCreateNew,
  placeholder,
}: EntityPickerProps) {
  const [query, setQuery] = useState('');
  const [entities, setEntities] = useState<EntityListItem[] | null>(_cache[type] ?? null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (entities) return;
    let cancelled = false;
    listEntities({ type })
      .then((rows) => {
        if (cancelled) return;
        _cache[type] = rows;
        setEntities(rows);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [type, entities]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (entities ?? []).filter((e) => e.id !== excludeEntityId);
    if (!q) return list.slice(0, 20);
    return list
      .filter((e) => e.name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [entities, query, excludeEntityId]);

  const trimmed = query.trim();
  const exactMatch = filtered.find((e) => e.name.toLowerCase() === trimmed.toLowerCase());
  const canCreateNew = trimmed.length > 0 && !exactMatch;

  function handleKey(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) {
        onPickExisting(filtered[0]!);
      } else if (canCreateNew) {
        onCreateNew(trimmed);
      }
    }
  }

  return (
    <div class="entity-picker">
      <div class="entity-picker-search">
        <Icons.Search size={14} />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder ?? `Search ${type}s…`}
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          onKeyDown={handleKey}
        />
      </div>

      {error && <div class="error-banner" style={{ margin: 12 }}>{error}</div>}

      {entities === null && !error && (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            fontFamily: 'var(--f-mono)',
            fontSize: 11,
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Loading {type}s…
        </div>
      )}

      {entities !== null && (
        <div class="entity-picker-list">
          {filtered.map((e) => (
            <button
              key={e.id}
              type="button"
              class="entity-picker-row"
              onClick={() => onPickExisting(e)}
            >
              <Avatar name={e.name} size={22} />
              <span class="entity-picker-row-name">{e.name}</span>
              {e.metadata.industry && (
                <span
                  class="mono"
                  style={{ fontSize: 10.5, color: 'var(--text-muted)' }}
                >
                  {String(e.metadata.industry)}
                </span>
              )}
            </button>
          ))}
          {filtered.length === 0 && !canCreateNew && (
            <div
              style={{
                padding: '20px 14px',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontFamily: 'var(--f-serif)',
                fontStyle: 'italic',
                fontSize: 14,
              }}
            >
              {entities.length === 0
                ? `No ${type}s in your portfolio yet.`
                : `No ${type}s match "${query}".`}
            </div>
          )}
          {canCreateNew && (
            <button
              type="button"
              class="entity-picker-create"
              onClick={() => onCreateNew(trimmed)}
            >
              <Icons.Plus size={14} />
              <span>
                Create <strong>"{trimmed}"</strong> as new {type}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
